// src/components/marketplace/ContactModal.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import * as marketplaceApi from '../../services/marketplace';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  itemType: 'project' | 'investor_request';
  itemId: string;
  itemName: string;
  ownerName: string;
  toUserId: string;
}

const ContactModal: React.FC<ContactModalProps> = ({
  open,
  onClose,
  itemType,
  itemId,
  itemName,
  ownerName,
  toUserId
}) => {
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await marketplaceApi.createContactRequest({
        toUserId,
        itemType,
        itemId,
        message: message.trim(),
        contactInfo: {
          email: contactEmail.trim(),
          phone: contactPhone.trim()
        }
      });

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setContactEmail('');
    setContactPhone('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Express Interest
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You are contacting <strong>{ownerName}</strong> regarding:
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
            {itemName}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your interest has been sent successfully! The owner will be notified.
            </Alert>
          )}

          {!success && (
            <>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message *"
                placeholder="Introduce yourself and explain your interest..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
              />

              <TextField
                fullWidth
                type="email"
                label="Your Email (optional)"
                placeholder="your@email.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Your Phone (optional)"
                placeholder="+1234567890"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                disabled={loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !message.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Interest Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactModal;
